<script>
  import { onMount, onDestroy } from 'svelte';
  import { selectedStudy, selectedSite, selectedParticipant } from '../stores/selectionStore';
  import { userRole, user, userRoles, userReadOnly } from '../stores/sessionStore';
  import { assignmentForProtocol } from '$utils/userAssignments.js';
  import { hasAccessUiAction } from '$utils/permissionUtils.js';
  import { studyProperties } from '../stores/configStore';
  import { isCompactMode } from '../stores/uiStore';
  import SiteFiles from './SiteFiles.svelte';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import { faEllipsis, faChevronDown } from '@fortawesome/free-solid-svg-icons';
  import { faPaperclip, faPencil, faHistory } from '@fortawesome/pro-light-svg-icons';
  import { fade, slide } from 'svelte/transition';
  import { createEventDispatcher } from 'svelte';
  const dispatch = createEventDispatcher();
  const demoMode = import.meta.env.VITE_DEMO_MODE === 'true';

  // Prop to control whether the options menu is shown
  export let showOptionsMenu = false;
  $: canViewAuditLogs = hasAccessUiAction(
    'sites.details.viewAuditLogs',
    $userRoles,
    $studyProperties,
    $userReadOnly
  );
  $: canEditSite = hasAccessUiAction(
    'sites.details.edit',
    $userRoles,
    $studyProperties,
    $userReadOnly
  );

  let localTime = '';
  let timeInterval;

  // Function to update local time
  const updateLocalTime = () => {
    if ($selectedSite && $selectedSite.timezoneCode) {
      const options = {
        timeZone: $selectedSite.timezoneCode,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      };
      const formatter = new Intl.DateTimeFormat([], options);
      localTime = formatter.format(new Date());
    } else {
      // Fallback to UTC if no timezone is available
      localTime = new Date().toLocaleString();
    }
  };

  // Initialize local time on mount + re-run whenever $selectedSite.timezoneCode
  // changes (e.g., user edits the site's timezone). Without this reactive
  // guard the 1s interval would eventually pick it up, but only if it's still
  // running AND the store actually got refreshed. Belt + suspenders.
  $: if ($selectedSite?.timezoneCode) {
    updateLocalTime();
  }

  // Debug logging to understand the issue
  // $: if ($selectedSite) {
  //   console.log('SiteDetails - selectedSite data:', {
  //     siteId: $selectedSite.siteId,
  //     siteName: $selectedSite.siteName,
  //     timezoneCode: $selectedSite.timezoneCode,
  //     timezoneGUID: $selectedSite.timezoneGUID,
  //     timezoneLabel: $selectedSite.timezoneLabel,
  //     userRole: $userRole,
  //     hasTimezoneCode: !!$selectedSite.timezoneCode
  //   });
  // }

  // Update local time every second
  onMount(() => {
    timeInterval = setInterval(updateLocalTime, 1000);
    document.addEventListener('click', handleClickOutside);
    //console.log('selectedSite:', $selectedSite);
    
    // Fetch user's assigned sites if they have multiple sites
    if (showSiteSwitcher) {
      fetchUserAssignedSites();
    }
    
    return () => {
      clearInterval(timeInterval);
      document.removeEventListener('click', handleClickOutside);
    };
  });

  // Helper function to check if user has required role
  const hasRequiredRole = () => {
    //console.log('userRole: ', $userRole);
    return $userRole === 'admin' || $userRole === 'support';
  };

  let showFiles = false;
  let showMenu = false;
  let isSiteFilesEnabled = false;
  let userAssignedSites = [];
  let showSiteSwitcher = false;
  let isSiteSwitcherOpen = false;
  let isLoadingSites = false;

  $: if ($studyProperties?.features?.documentManagement?.site !== undefined) {
    isSiteFilesEnabled = $studyProperties.features.documentManagement.site;
  }

  $: if (isSiteFilesEnabled && $studyProperties?.features?.documentManagement?.autoShowSite) {
    showFiles = true;
  }

  // Check if the current study has multiple assigned sites
  $: currentSiteAssignment = assignmentForProtocol($user, $selectedStudy?.protocolNumber);
  $: showSiteSwitcher = !!(currentSiteAssignment && currentSiteAssignment.siteIds.length > 1);

  let fetchedSitesKey = '';
  $: {
    const key = `${$selectedStudy?._id || ''}:${(currentSiteAssignment?.siteIds || []).join(',')}`;
    if (showSiteSwitcher && $selectedStudy?._id) {
      if (key !== fetchedSitesKey) {
        fetchedSitesKey = key;
        fetchUserAssignedSites();
      }
    } else {
      fetchedSitesKey = '';
      userAssignedSites = [];
    }
  }

  function handleMenuClick(event) {
    event.stopPropagation();
    showMenu = !showMenu;
  }

  function handleMenuClose() {
    showMenu = false;
  }

  // Add click handler to close menu when clicking outside
  function handleClickOutside(event) {
    if (showMenu && !event.target.closest('.menu-container')) {
      handleMenuClose();
    }
    if (isSiteSwitcherOpen && !event.target.closest('.site-switcher-container') && !event.target.closest('.site-dropdown')) {
      isSiteSwitcherOpen = false;
    }
  }

  // Fetch user's assigned sites for the current study
  async function fetchUserAssignedSites() {
    const siteIds = currentSiteAssignment?.siteIds || [];
    if (siteIds.length <= 1 || !$selectedStudy?._id) {
      userAssignedSites = [];
      return;
    }

    try {
      isLoadingSites = true;
      const sitePromises = siteIds.map(async (siteId) => {
        const response = await fetch(`/api/v1/sites/byId/${siteId}?studyGUID=${$selectedStudy._id}`);
        if (response.ok) {
          const data = await response.json();
          return data.data; // The API returns site data wrapped in a 'data' property
        }
        return null;
      });

      const sites = await Promise.all(sitePromises);
      userAssignedSites = sites.filter(site => site !== null);
    } catch (error) {
      console.error('Error fetching user assigned sites:', error);
      userAssignedSites = [];
    } finally {
      isLoadingSites = false;
    }
  }

  // Handle site switching
  async function handleSiteSwitch(newSite) {
    if (newSite && newSite._id !== $selectedSite._id) {
      selectedSite.set(newSite);
      isSiteSwitcherOpen = false;
      
      // Dispatch event to notify parent components that site has changed
      dispatch('siteChanged', newSite);
    }
  }

  // Toggle site switcher dropdown
  function toggleSiteSwitcher(event) {
    event.stopPropagation();
    isSiteSwitcherOpen = !isSiteSwitcherOpen;
  }

  // Get dropdown position relative to viewport
  function getDropdownPosition() {
    const button = document.querySelector('.site-switcher-container button');
    if (!button) {
      return { top: 0, left: 0, width: 200 };
    }
    
    const rect = button.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    
    // Calculate position below the button
    let top = rect.bottom + window.scrollY + 4; // 4px gap
    let left = rect.left + window.scrollX;
    let width = Math.max(rect.width, 200); // Minimum width of 200px
    
    // Adjust if dropdown would go off the right edge
    if (left + width > viewportWidth - 16) { // 16px margin
      left = viewportWidth - width - 16;
    }
    
    // Adjust if dropdown would go off the left edge
    if (left < 16) {
      left = 16;
    }
    
    // If dropdown would go off the bottom, position it above the button
    const dropdownHeight = Math.min(userAssignedSites.length * 60 + 16, 192); // Approximate height
    if (top + dropdownHeight > viewportHeight + window.scrollY - 16) {
      top = rect.top + window.scrollY - dropdownHeight - 4;
    }
    
    return { top, left, width };
  }
</script>

{#if $selectedSite}
  <div class="mx-1 mt-3 mb-0 p-4 rounded-lg shadow-md overflow-y-auto relative">
    <div
      class="absolute inset-0 bg-[url('/title_bg_light.jpg')] dark:bg-[url('/title_bg_dark.jpg')] bg-cover bg-center bg-no-repeat opacity-50 dark:opacity-75"
    ></div>

    <!-- Vertical Site Label -->
    <div class="absolute left-0 top-0 bottom-0 flex items-center justify-center w-8 z-20 bg-gray-100 dark:bg-gray-800">
      <div class="writing-mode-vertical text-xs font-black text-gray-600 dark:text-gray-300 tracking-wider">
        SITE
      </div>
    </div>

    <div class="relative z-10 ml-8">
      {#if showOptionsMenu}
        <div class="menu-container absolute right-1 top-0 flex-none">
          <button
            on:click={handleMenuClick}
            class="w-8 h-8 text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 rounded-lg hover:text-sky-800 dark:hover:text-white/70"
            title="More options"
          >
            <FontAwesomeIcon icon={faEllipsis} />
          </button>
          {#if showMenu}
            <div
              class="fixed transform -translate-x-full mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg py-1 z-[9999]"
            >
              {#if isSiteFilesEnabled}
                <button
                  on:click={() => {
                    showFiles = !showFiles;
                    handleMenuClose();
                  }}
                  class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                >
                  <span>{showFiles ? 'Hide documents' : 'Show documents'}</span>
                  <FontAwesomeIcon icon={faPaperclip} class="text-sky-800 dark:text-white/70" />
                </button>
              {/if}
              <button
                on:click={() => {
                  console.log('Edit site button clicked');
                  dispatch('edit', $selectedSite);
                  handleMenuClose();
                }}
                class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                disabled={!canEditSite}
                class:opacity-50={!canEditSite}
              >
                <span>Edit site</span>
                <FontAwesomeIcon icon={faPencil} class="text-sky-800 dark:text-white/70" />
              </button>
              <button
                on:click={() => {
                  console.log('Site audit log button clicked');
                  dispatch('auditLog', $selectedSite);
                  handleMenuClose();
                }}
                class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                disabled={!canViewAuditLogs}
                class:opacity-50={!canViewAuditLogs}
              >
                <span>View site audit logs</span>
                <FontAwesomeIcon icon={faHistory} class="text-sky-800 dark:text-white/70" />
              </button>
            </div>
          {/if}
        </div>
      {/if}
      <div class="grid grid-cols-5 gap-3 mt-0">
        <div>
          <!-- svelte-ignore a11y-label-has-associated-control -->
          <label class="block text-xs text-gray-900 dark:text-gray-200 whitespace-nowrap truncate">Site ID</label>
          {#if showSiteSwitcher}
            <div class="site-switcher-container relative">
              <button
                on:click={toggleSiteSwitcher}
                class="flex items-center justify-between text-left text-gray-900 dark:text-gray-100 whitespace-nowrap truncate rounded px-0 py-1"
                title="Switch to another assigned site"
                disabled={isLoadingSites}
              >
                <span class="truncate pl-0">{$selectedSite.siteId}</span>
                {#if isLoadingSites}
                  <div class="ml-5 w-3 h-3 border border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                {:else}
                  <FontAwesomeIcon icon={faChevronDown} class="ml-5 text-xs flex-shrink-0" />
                {/if}
              </button>
              {#if isSiteSwitcherOpen && !isLoadingSites}
                <div class="site-dropdown fixed bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg z-[9999] max-h-48 overflow-y-auto min-w-48"
                     style="top: {getDropdownPosition().top}px; left: {getDropdownPosition().left}px; width: {getDropdownPosition().width}px;">
                  {#if userAssignedSites.length > 1}
                    {#each userAssignedSites as site}
                      <button
                        on:click={() => handleSiteSwitch(site)}
                        class="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                      >
                        <div class="flex flex-col">
                          <span class="font-medium">{site.siteId}</span>
                          <span class="text-xs text-gray-500 dark:text-gray-400 truncate">{site.siteName}</span>
                        </div>
                        {#if site._id === $selectedSite._id}
                          <span class="text-blue-600 dark:text-blue-400 text-xs">Current</span>
                        {/if}
                      </button>
                    {/each}
                  {:else}
                    <div class="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                      No other sites available
                    </div>
                  {/if}
                </div>
              {/if}
            </div>
          {:else}
            <div class="text-gray-900 dark:text-gray-100 whitespace-nowrap truncate">{$selectedSite.siteId}</div>
          {/if}
        </div>
        <div>
          <!-- svelte-ignore a11y-label-has-associated-control -->
          <label class="block text-xs text-gray-900 dark:text-gray-200 whitespace-nowrap truncate">Country</label>
          <div class="text-gray-900 dark:text-gray-100 whitespace-nowrap truncate">{$selectedSite?.country || 'N/A'}</div>
        </div>
        <div>
          <!-- svelte-ignore a11y-label-has-associated-control -->
          <label class="block text-xs text-gray-900 dark:text-gray-200 whitespace-nowrap truncate">Timezone</label>
          <div class="text-gray-900 dark:text-gray-100 whitespace-nowrap truncate">{$selectedSite.timezoneCode || $selectedSite.timezoneLabel || 'N/A'}</div>
        </div>
        <div class="col-span-2">
          <!-- svelte-ignore a11y-label-has-associated-control -->
          <label class="block text-xs text-gray-900 dark:text-gray-200 whitespace-nowrap truncate">Local Time</label>
          <div class="text-gray-900 dark:text-gray-100 whitespace-nowrap truncate">{localTime}</div>
        </div>
      </div>
      {#if !$isCompactMode}
        <div class="grid grid-cols-5 gap-3 mt-2">
          <div class="col-span-2">
            <!-- svelte-ignore a11y-label-has-associated-control -->
            <label class="block text-xs text-gray-900 dark:text-gray-200 whitespace-nowrap truncate">Name</label>
            <div class="text-gray-900 dark:text-gray-100 whitespace-nowrap truncate">{$selectedSite.siteName}</div>
          </div>
          <div>
            <!-- svelte-ignore a11y-label-has-associated-control -->
            <label class="block text-xs text-gray-900 dark:text-gray-200 whitespace-nowrap truncate">Primary Contact</label>
            <div class="text-gray-900 dark:text-gray-100 whitespace-nowrap truncate">
              {$selectedSite.primaryInvestigator ? $selectedSite.primaryInvestigator : ''}
            </div>
          </div>
          <div>
            <!-- svelte-ignore a11y-label-has-associated-control -->
            <label class="block text-xs text-gray-900 dark:text-gray-200 whitespace-nowrap truncate">Email</label>
            <div class="text-gray-900 dark:text-gray-100 flex items-center min-w-0">
              {#if $selectedSite.emails && typeof $selectedSite.emails === 'string' && $selectedSite.emails.trim()}
                {@const emailList = $selectedSite.emails.split(',').map(email => email.trim())}
                {@const firstEmail = emailList[0]}
                {@const additionalCount = emailList.length - 1}
                <span class="whitespace-nowrap truncate">{firstEmail}</span>{#if additionalCount > 0}<span class="text-xs text-gray-500 dark:text-gray-400 ml-1 flex-shrink-0">(+{additionalCount} more)</span>{/if}
              {:else}
                <span class="text-gray-500 dark:text-gray-400 whitespace-nowrap truncate">n/a</span>
              {/if}
            </div>
          </div>
        </div>
      {/if}
    </div>
  </div>

  {#if showFiles}
    <div class="mx-1 mt-3" in:fade={{ duration: 200 }} out:fade={{ duration: 200 }}>
      <div in:slide={{ duration: 200 }} out:slide={{ duration: 200 }}>
        <SiteFiles on:close={() => (showFiles = false)} />
      </div>
    </div>
  {/if}
{/if}

<style>
  .writing-mode-vertical {
    writing-mode: vertical-rl;
    text-orientation: mixed;
    transform: rotate(180deg);
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  .animate-spin {
    animation: spin 1s linear infinite;
  }
</style>
